import { Address, JarvisProduct } from '@vfde-sails/avc-core';
import { getCMSConfig } from './cms';
import { Price } from '@vfde-brix/ws10/core';
import { PLACEHOLDER_KEY_REGEX } from '../constants';
import { FootnotePlaceholder, ProductPlaceholder } from '../enums/placeholder';

type Placeholders = FootnotePlaceholder | ProductPlaceholder;
export type PlaceholderMap = { [key in Placeholders]?: string };

/**
 * Replace every placeholders in a text by the given map (lookup table)
 * @param text The text  in which we are looking for the keys
 * @param map A map containing the placeholders and their corresponding new values
 * @returns The text with the exchanged placeholders
 */
export const replaceAllPlaceholders = (text: string, map: PlaceholderMap) =>
    text.replace(PLACEHOLDER_KEY_REGEX, (key, self) => key in map ? map[key as keyof PlaceholderMap] : self);

export const convertToPriceString = (price: number | string, showEmptyCents: boolean = false) => {
    const brixPrice = Price.fromNumber(+price.toString().replace(',', '.'));

    return (!showEmptyCents && !brixPrice.cent)
        ? brixPrice.euro.toString()
        : brixPrice.toString();
};

/**
 * Returns text with formatted product name.
 *
 * @param text
 * @param productName
 * @returns string
 */
export const formatProductName = (text: string, productName?: string) =>
    text.replace(ProductPlaceholder.PRODUCT, productName || '');

/**
 * Returns text with formatted download speed.
 *
 * @param text
 * @param download
 * @returns string
 */
export const formatDownload = (text: string, download?: string | number) =>
    text.replace(ProductPlaceholder.DOWNLOAD, download?.toString() || '');

/**
 * Returns text with formatted price.
 * @param text
 * @param price
 * @returns string
 */
export const formatPrice = (text: string, price: string | number, showEmptyCents: boolean = false) =>
    text.replace(ProductPlaceholder.PRICE, convertToPriceString(price, showEmptyCents));

export const formatEuro = (text: string) => text.replace('&euro;', '€');

// Tracking prices needs to be in format '60.00'
export const formatNumberToDecimalString = (price: number | string) =>
    parseFloat(price.toString()).toFixed(2);

export const formatPriceLegalText = (
    month: number,
): string => {
    const { regularPrice: text = '' } = getCMSConfig().offerSummaryCard.priceText;

    return replaceAllPlaceholders(text, {
        [ProductPlaceholder.MONTH]: (month + 1).toString(),
    });
};

/**
 * Returns formatted address text from Address object
 *
 * @param address Address
 * @returns string
 * @example '12345 City, Streetname 1a'
 */
export const formatAddressText = (address?: Address) => address
    ? `${address.zipcode} ` +
        `${address.city}, ` +
        `${address.street ?? ''} ` +
        `${address.housenumber ?? ''}` +
        `${address.housenumberextra ?? ''}`
    : '';

/**
 * Returns formatted tariff details text.
 * @param download download speed
 * @param upload upload speed
 * @returns string
 */
export const formatTariffDetails = (download: string | number, upload: string) => {
    const cmsText = getCMSConfig().tariffDetails.sublineText;

    return replaceAllPlaceholders(cmsText, {
        [ProductPlaceholder.DOWNLOAD]: download.toString(),
        [ProductPlaceholder.UPLOAD]: upload,
    });
};

/**
 * Format the content of an array into *"item1, item2 and/or item3"* string
 * @param unformattedText Any array
 * @param logicalOperator Any string. This will be the logical operator in the list.
 * Example: "oder", "und", "and", "or", "nor"
 */
export const formatReadableList = (array: Array<any>, logicalOperator: string) => {
    if (1 >= array.length) {
        return array.toString();
    }

    const text = array.toString().split(',').join(', ');
    const lastCommaIndex = text.lastIndexOf(',');

    return `${text.slice(0, lastCommaIndex)} ${logicalOperator}${text.slice(lastCommaIndex + 1)}`;
};

// Note: Need this to fulfill AC from the footnote Briefing
export const getLowerTariffSpeeds = (tariffs: JarvisProduct[], currentSpeed: string) =>
    tariffs.map(tariff => +tariff.download)
        .sort((x, y) => y - x)
        .filter((x) => x < +currentSpeed);

const removeDuplicatedItems = (array: Array<any>) => [...new Set(array)];

// Note: Need this to fulfill AC from the footnote Briefing
const removeCommonPrefixes = (array: string[]) => {
    if (!array.length) {
        return;
    }

    const firstPrefix = array[0].split(' ')[0];
    const hasCommonPrefix = array.every(item => firstPrefix === item.split(' ')[0]);

    if (!hasCommonPrefix) {
        return;
    }

    array.forEach((item, index) =>
        (0 !== index) && (array[index] = item.replace(firstPrefix, '').trim())
    );
};

// Note: Need this to fulfill AC from the footnote Briefing
export const formatDeviceNamesText = (product: JarvisProduct) => {
    const { devices } = product;
    const excludedList = (getCMSConfig().footnoteTariffItem.excludeDevices).split(',');

    if (!devices) {
        return '';
    }

    const relevantDeviceNames = removeDuplicatedItems(devices
        .filter(device => !excludedList.includes(device.id))
        .map(device => device.name)
        .sort()
        .reverse()
    );

    removeCommonPrefixes(relevantDeviceNames);

    return formatReadableList(relevantDeviceNames, 'oder');
};

/**
 * Formats the given string to lowercase, separates words
 * if they are next to each other without space and removes
 * any additional whitespaces and replaces the special
 * German letters in the text to basic latin letters
 * (e.g. `ß` to `ss`, `ä` to `ae`, and so on).
 * This step is requested by Digital Power,
 * to process the incoming data more easily.
 */
export const formatToTrackingString = (text: string, maxLength = 100): string =>
    text.replace(/([a-z])([A-Z])/g, '$1 $2')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ')
        .substring(0, maxLength)
        .replace(/\u00e4/g, 'ae')
        .replace(/\u00f6/g, 'oe')
        .replace(/\u00fc/g, 'ue')
        .replace(/\u00df/g, 'ss');
